import React from 'react'
import {
  Box,
  List,
  CircularProgress,
  Toolbar,
  SvgIcon,
  useMediaQuery,
} from '@mui/material'
import { useTheme, styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { KEYCLOAK_ROLES } from 'helpers/constants'
import RenderOnRole from 'components/access/RenderOnRole'
import { RouteOption, RouteAction } from './types'
import { Access } from 'types'
import DrawerLink from './DrawerLink'
import DrawerButton from './DrawerButton'

import { ReactComponent as AccountSVG } from 'assets/svg/new-icons/account.svg'
import { ReactComponent as LogoutSVG } from 'assets/svg/new-icons/logout.svg'
import { ReactComponent as MenuSVG } from 'assets/svg/new-icons/menu.svg'
import { ReactComponent as AdsSVG } from 'assets/svg/new-icons/ads.svg'
import { ReactComponent as AgentsSVG } from 'assets/svg/new-icons/agents.svg'
import { ReactComponent as BurgerClosedSVG } from 'assets/svg/new-icons/closedmenu.svg'
import { ReactComponent as BurgerMenuSVG } from 'assets/svg/new-icons/openmenu.svg'
import { ReactComponent as NewUserSVG } from 'assets/svg/new-icons/newuser.svg'
import { ReactComponent as ToolsSVG } from 'assets/svg/new-icons/tools.svg'
import { ReactComponent as UsersSVG } from 'assets/svg/new-icons/users.svg'
import { ReactComponent as AddPropertyAdSVG } from 'assets/svg/new-icons/newPropertyAd.svg'

import AuthenticationService from 'api/services/Authentication'
import { useDrawerStore } from 'store'
import KeycloakService from 'services/KeycloakService'
import { useNavigate } from 'react-router-dom'
interface DrawerHeaderProps {
  width: number
}

interface Props {
  width: number
}

function Contents(props: Props) {
  const { width } = props
  const { t } = useTranslation()
  const [open, toggleOpen, setOpen] = useDrawerStore((state) => [
    state.open,
    state.toggleOpen,
    state.setOpen,
  ])

  const toggleDrawer = () => {
    toggleOpen(open)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const routes: RouteOption[] = [
    {
      label: t('dashboard.drawer.links.profile.label'),
      icon: (
        <SvgIcon
          component={AccountSVG}
          inheritViewBox
          fill="black"
          sx={{
            width: { xs: '20px', sm: '33px' },
            height: { xs: '20px', sm: '32px' },
            fill: 'transparent',
          }}
        />
      ),
      to: 'profile',
      access: [KEYCLOAK_ROLES.ROLE_USER, KEYCLOAK_ROLES.ROLE_AGENT] as Access,
    },
    {
      label: t('dashboard.drawer.links.lists.label'),
      to: 'lists',
      end: false,
      icon: (
        <SvgIcon
          component={MenuSVG}
          inheritViewBox
          fill="black"
          sx={{
            width: { xs: '20px', sm: '32px' },
            height: { xs: '20px', sm: '32px' },
            fill: 'transparent',
          }}
        />
      ),
      access: [KEYCLOAK_ROLES.ROLE_ADMIN, KEYCLOAK_ROLES.ROLE_USER] as Access,
    },

    {
      icon: (
        <SvgIcon
          component={UsersSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            width: { xs: '20px', sm: '35px' },
            height: { xs: '20px', sm: '35px' },
          }}
        />
      ),
      label: t('dashboard.drawer.links.individuals.label'),
      to: 'users',
      access: [KEYCLOAK_ROLES.ROLE_ADMIN] as Access,
    },
    {
      icon: (
        <SvgIcon
          component={NewUserSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            width: { xs: '20px', sm: '28px' },
            height: { xs: '20px', sm: '35px' },
          }}
        />
      ),
      label: t('dashboard.drawer.links.addUser.label'),
      to: 'users/new',
      access: [KEYCLOAK_ROLES.ROLE_ADMIN] as Access,
    },
    {
      label: t('dashboard.drawer.links.brokers.label'),
      icon: (
        <SvgIcon
          component={AgentsSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            width: { xs: '20px', sm: '35px' },
            height: { xs: '20px', sm: '35px' },
          }}
        />
      ),
      to: 'agents',
      access: [KEYCLOAK_ROLES.ROLE_ADMIN] as Access,
    },
    {
      label: t('dashboard.drawer.links.myAds.label'),
      icon: (
        <SvgIcon
          component={AdsSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            width: { xs: '20px', sm: '29px' },
            height: { xs: '20px', sm: '30px' },
          }}
        />
      ),
      to: 'my-property-ads',
      access: [KEYCLOAK_ROLES.ROLE_AGENT] as Access,
    },
    {
      icon: (
        <SvgIcon
          component={UsersSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            width: { xs: '20px', sm: '32px' },
            height: { xs: '20px', sm: '32px' },
          }}
        />
      ),
      label: t('dashboard.drawer.links.usersList.label'),
      to: 'users',
      access: ['SuperAdmin'],
    },
    {
      label: t('dashboard.drawer.links.properties.label'),
      icon: (
        <SvgIcon
          component={AdsSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            width: { xs: '20px', sm: '32px' },
            height: { xs: '20px', sm: '32px' },
          }}
        />
      ),
      to: 'property-ads',
      access: [KEYCLOAK_ROLES.ROLE_ADMIN] as Access,
    },
    {
      label: t('dashboard.drawer.links.propertyAdsCreate.label'),
      icon: (
        <SvgIcon
          component={AddPropertyAdSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            width: { xs: '20px', sm: '29px' },
            height: { xs: '20px', sm: '28px' },
          }}
        />
      ),
      to: 'property-ads/create',
      access: [KEYCLOAK_ROLES.ROLE_AGENT] as Access,
    },
    {
      label: t('dashboard.drawer.links.tools.label'),
      icon: (
        <SvgIcon
          component={ToolsSVG}
          inheritViewBox
          fill="red"
          sx={{
            fill: 'red',
            width: { xs: '20px', sm: '35px' },
            height: { xs: '20px', sm: '35px' },
          }}
        />
      ),
      to: 'tools',
      access: [KEYCLOAK_ROLES.ROLE_ADMIN] as Access,
    },
  ]

  const navigate = useNavigate()

  const logoutMutation = AuthenticationService.hooks.useLogout({
    onSuccess: () => {
      navigate('/login')
    },
  })

  const actions: RouteAction[] = [
    {
      label: t('dashboard.drawer.links.logout.label'),
      icon: logoutMutation.isLoading ? (
        <CircularProgress style={{ color: 'primary' }} size={'32px'} />
      ) : (
        <SvgIcon
          component={LogoutSVG}
          inheritViewBox
          fill="black"
          sx={{
            width: { xs: '20px', sm: '43px' },
            height: { xs: '20px', sm: '32px' },
            fill: 'transparent',
          }}
        />
      ),
      handler: () => {
        console.log(KeycloakService.isLoggedIn())
        logoutMutation.mutate(null)
      },
    },
  ]
  const theme = useTheme()

  const SMALL_SCREEN = useMediaQuery(theme.breakpoints.down('md'))
  const MATCHES_SMD = useMediaQuery(theme.breakpoints.down('sm'))

  const DrawerHeader = styled('div', {
    shouldForwardProp: (prop) => prop !== 'width',
  })<DrawerHeaderProps>(({ theme, width }) => ({
    backgroundColor: theme.palette.common.white,
    zIndex: theme.zIndex.drawer + 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: SMALL_SCREEN ? '49px' : '56px',
    height: SMALL_SCREEN ? '49px' : '56px',
    overflow: 'visible',
    // position: 'absolute',
    // right: `calc(-56px)`,
    borderTopRightRadius: '12px',
    borderBottomRightRadius: '12px',
    border: '.5px solid #0909094D',
    borderLeft: 'none',
    borderTopColor: '#0909094D',
    borderTopWidth: '.5px',
    borderTopStyle: 'solid',
  }))
  return (
    <Box
      sx={{
        bgcolor: 'transparent',
        display: 'grid',
        gridTemplateColumns: 'auto 56px',
        gap: 0,
        pb: '25px',
        overflowY: 'auto',
        overflowX: 'visible',
      }}
    >
      <Box
        sx={{
          // height: '100%',
          // mt: '29px',
          // mb: '160px',
          // mt: KeycloakService.isAdmin() ? '56px' : '110px',
          maxWidth: open ? `${width}px` : `calc(80px)`,
          // maxHeight: 'calc(100% - 40px)',
          overflowY: 'auto',
          // overflowX: 'visible',
          bgcolor: 'common.white',
          borderBottomRightRadius: '20px',
          borderBottomLeftRadius: '20px',
          position: 'relative',
          boxShadow: '8px 8px 20px 0px #0909094D',
        }}
      >
        <Toolbar
          sx={{
            height: '49px',
            borderTopColor: '#0909094D',
            borderTopWidth: '.5px',
            borderTopStyle: 'solid',
          }}
        />
        <List
          component="nav"
          sx={{
            // height: '100%',
            // width: { xs: '236px', sm: '272px' },
            mt: KeycloakService.isAgent()
              ? MATCHES_SMD
                ? '59px'
                : '82px'
              : // ? '79px'
              // : '102px'
              KeycloakService.isOnlyUser()
              ? '66px'
              : undefined,
            py: open ? '8px' : '8px',
            px: { xs: open ? '16px' : 0, sm: open ? '20px' : 0 },
            overflowY: 'auto',
            overflowX: 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {routes.map((route, index) =>
            route?.to ? (
              <React.Fragment key={route.label + index + ''}>
                <RenderOnRole roles={route.access}>
                  <DrawerLink
                    key={route.label + index + 'link'}
                    open={open ?? false}
                    end={route?.end}
                    to={route?.to ? route.to : '#'}
                    primary={route.label}
                    icon={route.icon}
                  />
                </RenderOnRole>
              </React.Fragment>
            ) : null
          )}
          {actions.map((action, index) => {
            return (
              <DrawerButton
                key={action.label + index + 'button'}
                open={open ?? false}
                primary={action.label}
                icon={action.icon}
                onClick={action.handler}
              />
            )
          })}
        </List>
      </Box>
      <DrawerHeader width={width}>
        <SvgIcon
          onClick={open ? handleDrawerClose : toggleDrawer}
          component={open ? BurgerMenuSVG : BurgerClosedSVG}
          inheritViewBox
          fill="black"
          sx={{
            fill: 'transparent',
            cursor: 'pointer',
          }}
        />
      </DrawerHeader>
    </Box>
  )
}

export default Contents
